// Libs
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef } from "react";

// Components
import { Grow, type GrowProps } from "../animations";
import { Modal, type ModalProps } from "./Modal";

// Utils
import { getOffsetLeft, getOffsetTop, getTransformOriginValue } from "./_utils";

const MARGIN_THRESHOLD = 16;

function getElement(
    element:
        | (() => HTMLElement | undefined | null)
        | React.MutableRefObject<HTMLElement | undefined | null>
): HTMLElement | undefined | null {
    if (typeof element === "function") {
        return element();
    }

    return element.current;
}

export type VerticalTransform = "top" | "center" | "bottom";
export type HorizontalTransform = "left" | "center" | "right";

export interface AnchorOrigin {
    vertical: VerticalTransform;
    horizontal: HorizontalTransform;
}

const defaultAnchorOrigin: AnchorOrigin = {
    vertical: "bottom",
    horizontal: "left",
};

const defaultTransformOrigin: AnchorOrigin = {
    vertical: "top",
    horizontal: "left",
};

export interface DropdownProps extends React.PropsWithChildren<unknown> {
    /**
     * Anchoring element
     */
    anchorEl?:
        | (() => HTMLElement | undefined | null)
        | React.MutableRefObject<HTMLElement | undefined | null>;
    /**
     * Origin on anchoring element
     */
    anchorOrigin?: AnchorOrigin;
    /**
     * Additional className to dropdown
     */
    className?: string;
    /**
     * Additional className to content
     */
    contentClassName?: string;
    /**
     * Full width of content
     */
    hasFullWidth?: boolean;
    /**
     * Additional props for Grow
     */
    growProps?: GrowProps;
    /**
     * Additional className to modal
     */
    modalClassName?: string;
    /**
     * Additional props to modal
     */
    modalProps?: ModalProps;
    /**
     * If no spacing
     */
    noSpacing?: boolean;
    /**
     * Close handler
     */
    onClose?: ModalProps["onClose"];
    /**
     * Entering handler
     */
    onEntering?: GrowProps["onEntering"];
    /**
     * If is open
     */
    open?: boolean;
    /**
     * Transform of origin
     */
    transformOrigin?: AnchorOrigin;
    /**
     * If is invisible overlay
     */
    inputRef?: React.MutableRefObject<HTMLElement | undefined | null>;
}

export function Dropdown({
    anchorEl,
    anchorOrigin = defaultAnchorOrigin,
    children,
    className: classNameProp,
    contentClassName: contentClassNameProp,
    hasFullWidth = false,
    growProps,
    modalClassName: modalClassNameProp,
    modalProps,
    onClose,
    onEntering,
    open = false,
    noSpacing = false,
    transformOrigin = defaultTransformOrigin,
    inputRef = { current: undefined },
}: DropdownProps): JSX.Element {
    const modalClassName = clsx("ds-c-dropdown__modal", modalClassNameProp);
    const contentClassName = clsx(
        "ds-o-box",
        "ds-o-paper",
        "ds-o-paper--shadow",
        "ds-c-dropdown__content",
        contentClassNameProp,
        classNameProp
    );

    const childrenRef = useRef<HTMLDivElement>(null);

    const getAnchorOffset = useCallback(() => {
        const anchorRect = getElement(anchorEl)?.getBoundingClientRect();

        if (!anchorRect) {
            return;
        }

        return {
            top: anchorRect.top + getOffsetTop(anchorRect, anchorOrigin.vertical),
            left: anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal),
        };
    }, [anchorEl, anchorOrigin.vertical, anchorOrigin.horizontal]);

    const getTransformOrigin = useCallback(
        (elementRect) => {
            return {
                vertical: getOffsetTop(elementRect, transformOrigin.vertical),
                horizontal: getOffsetLeft(elementRect, transformOrigin.horizontal),
            };
        },
        [transformOrigin.vertical, transformOrigin.horizontal]
    );

    const getPositionStyle = useCallback((elementRect, transformOrigin, anchorOffset) => {
        const elementInput = inputRef.current;
        let top = anchorOffset.top - transformOrigin.vertical;
        let left = anchorOffset.left - transformOrigin.horizontal;
        const bottom = top + elementRect.height;
        const right = left + elementRect.width;
        let height = elementRect.height;
        let overflow = noSpacing ? "auto" : "initial";
        const heightThreshold =
            (noSpacing ? window.innerHeight * 2 : window.innerHeight) - MARGIN_THRESHOLD;
        const widthThreshold =
            (noSpacing ? window.innerWidth * 2 : window.innerWidth) - MARGIN_THRESHOLD;

        if (bottom > window.innerHeight && noSpacing) {
            height = elementRect.height - (bottom - window.innerHeight) - MARGIN_THRESHOLD;
            overflow = "scroll";
        }

        if (top < MARGIN_THRESHOLD) {
            const diff = top - MARGIN_THRESHOLD;
            top -= diff;
            transformOrigin.vertical += diff;
        } else if (bottom > heightThreshold) {
            const diff = bottom - heightThreshold;
            top -= diff;
            transformOrigin.vertical += diff;
        }

        if (left < MARGIN_THRESHOLD) {
            const diff = left - MARGIN_THRESHOLD;
            left -= diff;
            transformOrigin.horizontal += diff;
        } else if (right > widthThreshold) {
            const diff = right - widthThreshold;
            left -= diff;
            transformOrigin.horizontal += diff;
        }

        return {
            top: `${Math.round(top)}px`,
            left: `${Math.round(left)}px`,
            height: `${Math.round(height)}px`,
            width: hasFullWidth ? `${Math.round(elementInput?.offsetWidth)}px` : "initial",
            transformOrigin: getTransformOriginValue(transformOrigin),
            overflow: overflow,
        };
    }, []);

    const setPositionStyle = useCallback(() => {
        const element = getElement(childrenRef);

        if (!element) {
            return;
        }

        const elementRect = {
            width: element.offsetWidth,
            height: element.offsetHeight,
        };
        const transformOrigin = getTransformOrigin(elementRect);
        const anchorOffset = getAnchorOffset();

        const positioning = getPositionStyle(elementRect, transformOrigin, anchorOffset);

        element.style.top = positioning.top;
        element.style.left = positioning.left;
        element.style.transformOrigin = positioning.transformOrigin;
        if (hasFullWidth) {
            element.style.width = positioning.width;
        }
        if (noSpacing) {
            element.style.height = positioning.height;
            element.style.overflow = positioning.overflow;
        }
    }, [getTransformOrigin, getAnchorOffset, getPositionStyle]);

    useEffect(() => {
        setPositionStyle();
    }, [setPositionStyle]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleResize = debounce(() => setPositionStyle());
        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            handleResize.cancel();
            window.removeEventListener("resize", handleResize);
        };
    }, [open, setPositionStyle]);

    const handleEntering = (element: HTMLDivElement | null, isAppearing?: boolean) => {
        if (onEntering) {
            onEntering(element, isAppearing);
        }

        setPositionStyle();
    };

    return (
        <Modal
            className={modalClassName}
            open={open}
            onClose={onClose}
            invisibleOverlay
            {...modalProps}
        >
            <Grow {...growProps} appear in={open} onEntering={handleEntering}>
                <div className={contentClassName} ref={childrenRef}>
                    {children}
                </div>
            </Grow>
        </Modal>
    );
}
